import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import apiClient from "../apiClient";
import { formatMoney } from "../formatMoney";
import PageContext from "../PageContext.jsx";

const todayIso = () => new Date().toISOString().slice(0, 10);

const BankBalances = () => {
  const [searchParams] = useSearchParams();
  const selectedBankParam = searchParams.get("bankId") || "";
  const isSingleBankView = Boolean(selectedBankParam);
  const [banks, setBanks] = useState([]);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    BankID: selectedBankParam,
    BalanceDate: todayIso(),
    BalanceAmount: "",
  });

  const sortedBanks = useMemo(
    () => [...banks].sort((a, b) => (a.Description || "").localeCompare(b.Description || "")),
    [banks]
  );
  const selectedBank = useMemo(
    () => banks.find((bank) => String(bank.BankID) === String(form.BankID)) || null,
    [banks, form.BankID]
  );
  const visibleBalances = useMemo(() => {
    const rows = isSingleBankView
      ? balances.filter((row) => String(row.BankID) === String(selectedBankParam))
      : balances;
    return [...rows].sort(
      (left, right) =>
        String(right.BalanceDate || "").localeCompare(String(left.BalanceDate || "")) ||
        Number(right.BankBalanceID || 0) - Number(left.BankBalanceID || 0)
    );
  }, [balances, isSingleBankView, selectedBankParam]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [banksRes, balancesRes] = await Promise.all([
          apiClient.get("/banks"),
          apiClient.get("/bank-balances"),
        ]);
        const bankRows = Array.isArray(banksRes.data) ? banksRes.data : [];
        const balanceRows = Array.isArray(balancesRes.data) ? balancesRes.data : [];
        const defaultBankId = selectedBankParam || bankRows[0]?.BankID || "";
        setBanks(bankRows);
        setBalances(balanceRows);
        setForm((prev) => ({
          ...prev,
          BankID: isSingleBankView ? selectedBankParam : prev.BankID || defaultBankId,
        }));
      } catch (err) {
        setError(err?.response?.data?.message || "Failed to load bank balances.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isSingleBankView, selectedBankParam]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setEditingId(null);
    setFormError("");
    setForm((prev) => ({
      BankID: isSingleBankView
        ? selectedBankParam
        : prev.BankID || sortedBanks[0]?.BankID || "",
      BalanceDate: todayIso(),
      BalanceAmount: "",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError("");

    if (!form.BankID || !form.BalanceDate || form.BalanceAmount === "") {
      setFormError("Bank, balance date, and balance amount are required.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        BankID: form.BankID,
        BalanceDate: form.BalanceDate,
        BalanceAmount: form.BalanceAmount,
      };

      if (editingId) {
        await apiClient.put(`/bank-balances/${editingId}`, payload);
      } else {
        await apiClient.post("/bank-balances", payload);
      }

      const balancesRes = await apiClient.get("/bank-balances");
      setBalances(Array.isArray(balancesRes.data) ? balancesRes.data : []);
      resetForm();
    } catch (err) {
      setFormError(err?.response?.data?.message || "Could not save bank balance. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (row) => {
    setEditingId(row.BankBalanceID);
    setFormError("");
    setForm({
      BankID: String(row.BankID || ""),
      BalanceDate: row.BalanceDate || todayIso(),
      BalanceAmount: row.BalanceAmount != null ? String(row.BalanceAmount) : "",
    });
  };

  const handleDelete = async (bankBalanceId) => {
    try {
      await apiClient.delete(`/bank-balances/${bankBalanceId}`);
      setBalances((prev) => prev.filter((row) => row.BankBalanceID !== bankBalanceId));
      if (editingId === bankBalanceId) {
        resetForm();
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Could not delete bank balance.");
    }
  };

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Bank Balances</h1>
          <p className="page-subtitle"><Link to="/">Home</Link> / <Link to="/Banks">Banks</Link> / Balances</p>
        </div>
      </header>
      <PageContext
        items={[
          { label: "Home", to: "/" },
          { label: "Banks", to: "/Banks" },
          { label: selectedBank ? `${selectedBank.Description} Balances` : "Bank Balances" },
        ]}
        note={selectedBank ? `Add a new balance line or edit previous entries for ${selectedBank.Description}.` : "Enter or revise dated balance snapshots for each bank account."}
      />

      {error && <p className="notice notice-error">{error}</p>}

      <section className="panel form-card">
        <form className="form" onSubmit={handleSubmit}>
          {isSingleBankView ? (
            <>
              <label>Bank Account</label>
              <div className="form-value-preview">
                {selectedBank?.Description || "Selected bank"}
                {selectedBank?.CurrencyDescription ? ` (${selectedBank.CurrencyDescription})` : ""}
              </div>
            </>
          ) : (
            <>
              <label htmlFor="bank-balance-bank">Bank Account</label>
              <select id="bank-balance-bank" name="BankID" value={form.BankID} onChange={handleChange}>
                <option value="">Select bank</option>
                {sortedBanks.map((bank) => (
                  <option key={bank.BankID} value={bank.BankID}>
                    {bank.Description} {bank.CurrencyDescription ? `(${bank.CurrencyDescription})` : ""}
                  </option>
                ))}
              </select>
            </>
          )}

          <label htmlFor="bank-balance-date">Balance Date</label>
          <input id="bank-balance-date" type="date" name="BalanceDate" value={form.BalanceDate} onChange={handleChange} />

          <label htmlFor="bank-balance-amount">Bank Balance</label>
          <input
            id="bank-balance-amount"
            type="number"
            step="0.01"
            name="BalanceAmount"
            value={form.BalanceAmount}
            onChange={handleChange}
            placeholder="e.g. 250000.00"
          />
          <div className="form-value-preview">
            Display: {formatMoney(form.BalanceAmount || 0, selectedBank?.CurrencyDescription)}
          </div>

          <div className="form-actions">
            <button className="btn" type="submit" disabled={saving}>
              {editingId ? "Update Balance" : "Save Balance"}
            </button>
            {editingId ? (
              <button className="btn btn-secondary" type="button" onClick={resetForm}>Cancel Edit</button>
            ) : null}
          </div>
          {formError && <p className="notice notice-error">{formError}</p>}
        </form>
      </section>

      {loading ? (
        <p className="notice notice-muted">Loading bank balances...</p>
      ) : (
        <section className="resource-list">
          {visibleBalances.length === 0 ? (
            <p className="notice notice-muted">
              {isSingleBankView && selectedBank
                ? `No balances recorded yet for ${selectedBank.Description}. Add the first balance line above.`
                : "No bank balances recorded yet."}
            </p>
          ) : (
            visibleBalances.map((row) => (
              <article key={row.BankBalanceID} className="resource-row">
                <div>
                  <strong>{row.BankDescription}</strong>
                  <div className="resource-meta">Balance date: {row.BalanceDate}</div>
                  <div className="resource-meta">Balance: {formatMoney(row.BalanceAmount, row.CurrencyDescription)}</div>
                </div>
                <div className="actions">
                  <button className="btn btn-secondary" onClick={() => handleEdit(row)}>Update</button>
                  <button className="btn btn-danger" onClick={() => handleDelete(row.BankBalanceID)}>Delete</button>
                </div>
              </article>
            ))
          )}
        </section>
      )}
    </div>
  );
};

export default BankBalances;
